import { ITransformParams } from "../types/transform_params.js";
import { Easing, TransformBase, TransformType } from "./transform_base.js";

export interface CounterOptions
{
  from_value:      number;
  to_value:        number;
  duration_ms:     number;
  easing?:         Easing;
  decimal_places?: number;
  prefix?:         string;
  suffix?:         string;
  separator?:      string;
  onComplete?:     () => void;
}

export interface ITextDisplayable
{
  setText(text: string): void;
}

export class TextCounterTransform extends TransformBase
{
  private target: ITextDisplayable;
  private from_value: number;
  private to_value: number;
  private decimal_places: number;
  private prefix: string;
  private suffix: string;
  private separator: string;
  private onComplete?: () => void;

  constructor(
    target: ITextDisplayable,
    start_time: number,
    options: CounterOptions,
    transform_params: ITransformParams = {}
  )
  {
    transform_params.easing_pow = 2.0;

    super(
      TransformType.Counter,
      start_time,
      options.duration_ms,
      true,
      transform_params
    );

    this.target = target;
    this.from_value = options.from_value;
    this.to_value = options.to_value;
    this.decimal_places = options.decimal_places || 0;
    this.prefix = options.prefix || "";
    this.suffix = options.suffix || "";
    this.separator = options.separator || "";
    this.onComplete = options.onComplete;
  }

  protected getTarget(): ITextDisplayable
  {
    return this.target;
  }

  protected initImportant(): void
  {
    this.updateCounterText(0);
  }

  protected onUpdateTransform(progress: number): void
  {
    this.updateCounterText(progress);

    if (progress >= 1 && this.onComplete) {
      this.onComplete();
    }
  }

  private updateCounterText(progress: number): void
  {
    const current_value = this.from_value + (this.to_value - this.from_value) * progress;
    const formatted_value = this.formatNumber(current_value);
    const display_text = `${this.prefix}${formatted_value}${this.suffix}`;

    this.target.setText(display_text);
  }

  private formatNumber(value: number): string
  {
    let formatted = value.toFixed(this.decimal_places);

    if (this.separator) {
      const parts = formatted.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, this.separator);
      formatted = parts.join('.');
    }

    return formatted;
  }

  public getFromValue(): number { return this.from_value; }
  public getToValue(): number { return this.to_value; }

  public setToValue(newValue: number): void
  {
    this.to_value = newValue;
  }

  public toStrTransform(): string
  {
    return `TextCounterTransform(${this.from_value} -> ${this.to_value}, duration: ${this.getDuration()}ms)`;
  }
}
